import { updateProfileInfoThunk } from '../../../../../store/workspace/thunks';
import { useDispatch, useSelector } from '../../../../../store/store';
import { objectURLToFile } from '../../../../../utils/generalHelpers';
import { uploadFileThunk } from '../../../../../store/general/thunks';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { ListItem } from '../../../../../store/general/types';
import DropDown from '../../../../../components/DropDown';
import { companySizeList } from '../../../../../configs';
import Button from '../../../../../components/Button';
import Modal from '../../../../../components/Modal';
import Input from '../../../../../components/Input';
import { useNavigate } from 'react-router-dom';
import styles from './style.module.scss';
import { v4 as uuid } from 'uuid';
import {
    EditIcon,
    ImageIcon,
    WarningIcon,
    EditSquareIcon,
} from '../../../../../assets/icons';
import {
    selectProfile,
    selectLogoImage,
    selectCroppedCompanyLogo,
    selectUpdatingProfileInfo,
} from '../../../../../store/workspace/selectors';
import {
    setCompanyLogo,
    setCompanyLogoModal,
    setCroppedCompanyLogo,
    setEditCompanyInfoModal,
} from '../../../../../store/workspace/slice';

export default function EditCompanyInfo() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const profile = useSelector(selectProfile);
    const [website, setWebsite] = useState(profile?.companyWebsite || '');
    const [address, setAddress] = useState(profile?.address || '');
    const [city, setCity] = useState(profile?.city || '');
    const [country, setCountry] = useState(profile?.country || '');
    const [state, setState] = useState(profile?.state || '');
    const [zipCode, setZipCode] = useState(profile?.zipCode || '');
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [isValidURL, setIsValidURL] = useState(true);
    const isLoading = useSelector(selectUpdatingProfileInfo);
    const [companySize, setCompanySize] = useState<ListItem | null>(profile?.companySize || null);
    const croppedCompanyLogo = useSelector(selectCroppedCompanyLogo);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const logo = useSelector(selectLogoImage);

    useEffect(() => {
        return () => {
            dispatch(setCroppedCompanyLogo(null));
            dispatch(setCompanyLogo(null));
        }
    }, []);

    const navigateToSupport = () => {
        dispatch(setEditCompanyInfoModal(false));
        navigate('/workspace/support');
    };

    const onSelectFile = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                dispatch(setCompanyLogo(reader.result as string));
                dispatch(setCompanyLogoModal(true));
            });
            reader.readAsDataURL(e.target.files[0]);
        }
    };

    const submit = async () => {
        const body = {
            ...profile,
            companyWebsite: website,
            city,
            state,
            zipCode,
            country,
            address,
            companySize,
        };

        if (croppedCompanyLogo) {
            setUploadingLogo(true);
            const companyLogoKey = profile?.companyLogo || `${uuid()}.png`;
            const logoImageFile = await objectURLToFile(croppedCompanyLogo, companyLogoKey, 'image/png');

            logoImageFile && await dispatch(uploadFileThunk({
                file: logoImageFile,
                accessLevel: 'guest',
                fileName: `companies-logo-images/${companyLogoKey}`,
            }));

            body.companyLogo = companyLogoKey;
        }

        dispatch(updateProfileInfoThunk(body))
    };

    const triggerFileSelect = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    return (
        <Modal
            size='md'
            icon={<EditSquareIcon />}
            childClassName={styles.wrapper}
            title='Edit company information'
            closeFunc={() => dispatch(setEditCompanyInfoModal(false))}
        >
            <div className={styles.content}>
                <div className={styles.content_form}>
                    <Input
                        type='text'
                        label='Company website url'
                        value={website}
                        validation='URL'
                        setIsValid={setIsValidURL}
                        onChange={setWebsite}
                    />
                    <DropDown
                        label='Company Size'
                        dropDownList={companySizeList}
                        selectedItem={companySize}
                        setItem={setCompanySize}
                        titleKey='title'
                        idKey='id'
                    />
                    <Input
                        type='text'
                        label='Address'
                        placeholder=''
                        value={address}
                        onChange={setAddress}
                    />
                    <div className={styles.grid}>
                        <Input
                            type='text'
                            label='City'
                            value={city}
                            onChange={setCity}
                        />
                        <Input
                            type='text'
                            label='State'
                            value={state}
                            onChange={setState}
                        />
                        <Input
                            type='text'
                            label='Country'
                            value={country}
                            onChange={setCountry}
                        />
                        <Input
                            type='text'
                            label='Zip Code'
                            value={zipCode}
                            onChange={setZipCode}
                        />
                    </div>
                </div>
                <div className={styles.content_logo}>
                    <span>Company logo</span>
                    <div className={styles.content_logo_placeholder} onClick={triggerFileSelect}>
                        <input type="file" accept="image/*" onChange={onSelectFile} ref={fileInputRef} style={{ display: 'none' }} />
                        {croppedCompanyLogo ? <img src={croppedCompanyLogo} alt="" /> : logo ? <img src={logo} alt="" /> : <ImageIcon />}
                        <div className={styles.content_logo_placeholder_overlay}>
                            <EditIcon width={46} height={46} />
                        </div>
                    </div>
                </div>
                <div className={styles.description}>
                    <WarningIcon />
                    <span className={styles.description_text}>
                        To update your company name,
                        please reach out to our <b onClick={navigateToSupport}>support</b> team.
                    </span>
                </div>
            </div>
            <div className={styles.actions}>
                <Button
                    type='Outline-Primary'
                    text='Cancel'
                    className={styles.actions_btn}
                    onClick={() => dispatch(setEditCompanyInfoModal(false))}
                />
                <Button
                    type='Primary'
                    text='Confirm'
                    onClick={submit}
                    loading={isLoading || uploadingLogo}
                    className={styles.actions_btn}
                    disabled={!city ||
                        !isValidURL ||
                        !state ||
                        !zipCode ||
                        !country ||
                        !address ||
                        !companySize
                    }
                />
            </div>
        </Modal>
    )
}
